import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
} from 'recharts'
import { Flame, CalendarDays, Moon, type LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { HourlyHeatmapCell } from '@/types'
import { ExportButton } from './ExportButton'
import { ReportsExportSlot } from './ReportsExportSlot'
import { openPrintableReport, escapeHtml } from '@/lib/printReport'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { cn, formatDateHuman, formatDateRangeHuman, formatHour12 } from '@/lib/utils'

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DOW_LABELS_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]
const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => h)
const CURRENCY_SYMBOL: Record<string, string> = { PKR: 'Rs', USD: '$', EUR: '€', GBP: '£' }

type Metric = 'net' | 'orders'

interface Props {
  range: UseReportRange
}

export function HoursTab({ range }: Props) {
  const { formatCurrency, currencyCode } = useCurrency()
  const [metric, setMetric] = useState<Metric>('net')

  const hourlyQuery = useQuery({
    queryKey: ['reports-v2', 'hourly', range.fromISO, range.toISO],
    queryFn: () => apiClient.getHourlySalesReport(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  const data = hourlyQuery.data?.data
  const series = data?.series ?? []
  const heatmap = data?.heatmap ?? []

  // How many times each weekday (0=Sun…6=Sat) occurs in the selected range.
  // The backend returns per-(dow,hour) SUMS; dividing by these counts turns them
  // into a "typical day" average for that weekday — comparable to a daily target,
  // and not inflated just because the range is longer.
  const dowCounts = useMemo(() => {
    const counts = Array<number>(7).fill(0)
    const d = new Date(range.from)
    for (let i = 0; i < range.days; i++) {
      counts[d.getDay()]++
      d.setDate(d.getDate() + 1)
    }
    return counts
  }, [range.from, range.days])

  // Heatmap matrix [dow][hour], averaged to a typical day for that weekday.
  const matrix = useMemo(() => {
    const m: HourlyHeatmapCell[][] = Array.from({ length: 7 }, (_, dow) =>
      Array.from({ length: 24 }, (_, hour) => ({ dow, hour, orders: 0, net: 0 })),
    )
    heatmap.forEach((cell) => {
      if (m[cell.dow] && m[cell.dow][cell.hour]) {
        const divisor = Math.max(1, dowCounts[cell.dow] ?? 0)
        m[cell.dow][cell.hour] = {
          dow: cell.dow,
          hour: cell.hour,
          net: cell.net / divisor,
          orders: cell.orders / divisor,
        }
      }
    })
    return m
  }, [heatmap, dowCounts])

  const valueOf = (c: HourlyHeatmapCell) => (metric === 'net' ? c.net : c.orders)

  const max = useMemo(() => {
    let v = 0
    matrix.forEach((row) => row.forEach((c) => (v = Math.max(v, valueOf(c)))))
    return v
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, metric])

  const peak = useMemo<HourlyHeatmapCell | null>(() => {
    let best: HourlyHeatmapCell | null = null
    matrix.forEach((row) =>
      row.forEach((c) => {
        if (valueOf(c) > 0 && (best === null || valueOf(c) > valueOf(best))) best = c
      }),
    )
    return best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, metric])

  // Only render the hours the venue is actually open (any orders that hour across
  // the week). Trimming dead columns makes the remaining cells wide and legible.
  const activeHours = useMemo(() => {
    const present = new Set<number>()
    matrix.forEach((row) => row.forEach((c) => c.orders > 0 && present.add(c.hour)))
    if (present.size === 0) return [] as number[]
    const hrs = [...present].sort((a, b) => a - b)
    const lo = hrs[0]
    const hi = hrs[hrs.length - 1]
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  }, [matrix])

  // Per-weekday totals (a typical day's worth) → drives the "per day" bars and
  // the "Strongest day" hero stat.
  const dayTotals = useMemo(
    () => matrix.map((row) => row.reduce((s, c) => s + valueOf(c), 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matrix, metric],
  )
  const busiestDayIdx = useMemo(
    () => dayTotals.reduce((best, t, i) => (t > dayTotals[best] ? i : best), 0),
    [dayTotals],
  )
  const maxDayTotal = useMemo(() => Math.max(1, ...dayTotals), [dayTotals])

  // Quietest open hour (lowest activity among hours we're actually open).
  const quietest = useMemo<{ hour: number; value: number } | null>(() => {
    if (!activeHours.length) return null
    let best: { hour: number; value: number } | null = null
    activeHours.forEach((h) => {
      let tot = 0
      for (let d = 0; d < 7; d++) tot += valueOf(matrix[d][h])
      if (best === null || tot < best.value) best = { hour: h, value: tot }
    })
    return best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHours, matrix, metric])

  // ── number formatting ───────────────────────────────────────────────────────
  const symbol = CURRENCY_SYMBOL[currencyCode] ?? ''
  // Scannable, locale-aware magnitudes for axes / cells / bars (no symbol).
  const compactBare = (v: number) => formatCompact(v, metric === 'net' ? currencyCode : 'COUNT')
  // With currency symbol — used for the big hero numbers.
  const compactMoney = (v: number) => (metric === 'net' ? `${symbol} ${compactBare(v)}` : compactBare(v))
  // Exact value for hover tooltips (precision lives in the tooltip, not the chrome).
  const formatExact = (v: number) =>
    metric === 'net' ? formatCurrency(v) : (Math.round(v * 10) / 10).toLocaleString('en-US')
  const metricNoun = metric === 'net' ? 'net sales' : 'orders'

  const brand = useBusinessNameWithFallback()
  const handlePrintPdf = () => {
    openPrintableReport({
      title: 'Hourly Sales — typical day',
      subtitle: `${formatDateRangeHuman(range.from, range.to)} • Asia/Karachi • per-day averages`,
      bodyHtml: buildHourlyPdf(matrix, metric, formatCurrency),
      brand,
    })
  }

  const loading = hourlyQuery.isLoading
  const peakValue = peak ? valueOf(peak) : 0

  return (
    <div className="space-y-4">
      <ReportsExportSlot>
        <ExportButton
          report="hourly"
          reportLabel="Hourly sales"
          fromISO={range.fromISO}
          toISO={range.toISO}
          onPrintPdf={handlePrintPdf}
        />
      </ReportsExportSlot>

      {/* ── HERO: the insight, finally given the weight it deserves ───────────── */}
      <Card className="overflow-hidden duration-500 animate-in fade-in slide-in-from-bottom-2">
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50/50 to-transparent dark:from-amber-950/30 dark:via-orange-950/15 dark:to-transparent">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-14 h-44 w-44 rounded-full bg-amber-400/20 blur-3xl dark:bg-amber-500/10"
          />
          <CardContent className="relative p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-400">
                <Flame className="h-4 w-4" />
                Peak hours
              </div>
              <MetricToggle metric={metric} setMetric={setMetric} />
            </div>

            {loading ? (
              <Skeleton className="mt-5 h-20 w-full" />
            ) : peak ? (
              <>
                <div className="mt-5 grid gap-5 sm:grid-cols-[1.25fr_1fr_1fr]">
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <Flame className="h-3.5 w-3.5 text-amber-500" />
                      Busiest time
                    </div>
                    <div className="mt-1.5 text-2xl font-bold leading-none sm:text-[28px]">
                      {DOW_LABELS_FULL[peak.dow]}
                      <span className="text-amber-600 dark:text-amber-400">
                        {' · '}
                        {formatHour12(peak.hour)}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground tabular-nums">
                      <span className="font-semibold text-foreground">{compactMoney(peakValue)}</span>{' '}
                      avg {metricNoun}
                    </div>
                  </div>

                  <HeroStat
                    icon={CalendarDays}
                    label="Strongest day"
                    value={DOW_LABELS_FULL[busiestDayIdx]}
                    sub={`${compactMoney(dayTotals[busiestDayIdx])} / day`}
                  />
                  <HeroStat
                    icon={Moon}
                    label="Quietest open hour"
                    value={quietest ? formatHour12(quietest.hour) : '—'}
                    sub={quietest ? `${compactMoney(quietest.value / 7)} avg` : undefined}
                  />
                </div>

                <p className="mt-5 max-w-2xl text-sm text-muted-foreground">
                  Your week peaks on{' '}
                  <span className="font-medium text-foreground">
                    {DOW_LABELS_FULL[peak.dow]} around {formatHour12(peak.hour)}
                  </span>{' '}
                  (~{compactMoney(peakValue)}). Make sure you're well-staffed for it — and consider
                  a promo to lift your quiet stretch.
                </p>
              </>
            ) : (
              <div className="mt-4 text-sm text-muted-foreground">
                No sales in this range yet — pick a range with orders to see when you peak.
              </div>
            )}
          </CardContent>
        </div>
      </Card>

      {/* ── HEATMAP: trimmed, warm, anchored to the peak ─────────────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">When you're busy</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              A typical week, hour by hour — darker means busier.
            </p>
          </div>
          <HeatmapLegend max={max} compactBare={compactBare} />
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[280px] w-full" />
          ) : activeHours.length === 0 ? (
            <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
              No sales in this range yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-1">
                <thead>
                  <tr>
                    <th className="w-9" />
                    {activeHours.map((h) => (
                      <th
                        key={h}
                        className="px-0.5 text-center text-[13px] font-normal text-muted-foreground tabular-nums"
                      >
                        {formatHourShort(h)}
                      </th>
                    ))}
                    <th className="whitespace-nowrap pl-3 pr-1 text-left text-[13px] font-normal text-muted-foreground">
                      Per day
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, dow) => {
                    const isBusiest = dow === busiestDayIdx
                    return (
                      <tr key={dow}>
                        <th
                          className={cn(
                            'pr-2 text-right text-[13px]',
                            isBusiest ? 'font-bold text-foreground' : 'font-medium text-muted-foreground',
                          )}
                        >
                          {DOW_LABELS[dow]}
                        </th>
                        {activeHours.map((h) => {
                          const cell = row[h]
                          const value = valueOf(cell)
                          const ordersAvg = Math.round(cell.orders * 10) / 10
                          const isPeak = !!peak && peak.dow === dow && peak.hour === h
                          return (
                            <td
                              key={h}
                              title={`${DOW_LABELS_FULL[dow]} ${formatHour12(h)} — typical day: ${formatExact(value)} (${ordersAvg} order${ordersAvg === 1 ? '' : 's'})`}
                              className={cn(
                                'relative h-11 rounded-md transition-colors',
                                heatColor(value, max),
                                isPeak &&
                                  'z-10 ring-2 ring-amber-500 ring-offset-1 ring-offset-background',
                              )}
                            >
                              {isPeak && (
                                <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-bold leading-none text-white">
                                  {compactBare(value)}
                                </span>
                              )}
                            </td>
                          )
                        })}
                        <td className="pl-3 pr-1">
                          <DayTotalBar
                            value={dayTotals[dow]}
                            max={maxDayTotal}
                            label={compactBare(dayTotals[dow])}
                            highlight={isBusiest}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="mt-3 text-[13px] text-muted-foreground">
                The <span className="font-medium text-amber-600 dark:text-amber-400">ringed</span> cell
                is your single busiest weekday-and-hour. Bars on the right are each day's typical
                total. Blank cells are hours you're closed or dead.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── TREND: labeled axis + locale-aware compact numbers ───────────────── */}
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-base">Sales over time</CardTitle>
          <p className="text-[13px] text-muted-foreground">
            {metric === 'net' ? `Net sales per hour (${symbol})` : 'Orders per hour'} — hover any
            point for the exact day and time.
          </p>
        </CardHeader>
        <CardContent className="h-[280px]">
          {loading ? (
            <Skeleton className="h-full w-full" />
          ) : series.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No sales in this range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="hoursArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148,163,184,0.18)" vertical={false} />
                <XAxis
                  dataKey="hour_start"
                  fontSize={13}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={56}
                  tickFormatter={(iso: string) => {
                    const d = new Date(iso)
                    if (Number.isNaN(d.getTime())) return ''
                    return range.days <= 1 ? formatHour12(d.getHours()) : formatDateHuman(d)
                  }}
                />
                <YAxis
                  fontSize={13}
                  tickLine={false}
                  axisLine={false}
                  width={metric === 'net' ? 70 : 46}
                  tickFormatter={(v: number) => compactBare(v)}
                />
                <ReTooltip
                  contentStyle={{ borderRadius: 8, fontSize: 13 }}
                  labelFormatter={(iso: string) => {
                    const d = new Date(iso)
                    return Number.isNaN(d.getTime())
                      ? ''
                      : `${formatDateHuman(d)}, ${formatHour12(d.getHours())}`
                  }}
                  formatter={(v: number) => [formatExact(v), metric === 'net' ? 'Net sales' : 'Orders']}
                />
                <Area
                  type="monotone"
                  dataKey={metric}
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="url(#hoursArea)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function MetricToggle({
  metric,
  setMetric,
}: {
  metric: Metric
  setMetric: (m: Metric) => void
}) {
  return (
    <div className="flex items-center rounded-md bg-background/70 p-1 ring-1 ring-border backdrop-blur-sm">
      <Button
        type="button"
        size="sm"
        variant={metric === 'net' ? 'default' : 'ghost'}
        className="h-7 px-3 text-xs"
        onClick={() => setMetric('net')}
      >
        Net sales
      </Button>
      <Button
        type="button"
        size="sm"
        variant={metric === 'orders' ? 'default' : 'ghost'}
        className="h-7 px-3 text-xs"
        onClick={() => setMetric('orders')}
      >
        Orders
      </Button>
    </div>
  )
}

function HeroStat({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: LucideIcon
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="sm:border-l sm:border-border/60 sm:pl-5">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-amber-500" />
        {label}
      </div>
      <div className="mt-1.5 text-xl font-bold leading-tight">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">{sub}</div>}
    </div>
  )
}

function DayTotalBar({
  value,
  max,
  label,
  highlight,
}: {
  value: number
  max: number
  label: string
  highlight: boolean
}) {
  const pct = max > 0 && value > 0 ? Math.max(3, (value / max) * 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 min-w-[36px] flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full',
            highlight ? 'bg-amber-500' : 'bg-amber-300 dark:bg-amber-700',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={cn(
          'w-16 text-right text-xs tabular-nums',
          highlight ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
    </div>
  )
}

function HeatmapLegend({
  max,
  compactBare,
}: {
  max: number
  compactBare: (v: number) => string
}) {
  // Five swatches matching the thresholds in heatColor so a new user can decode
  // any cell at a glance. The endpoint is labelled with the busiest value.
  const swatches = [0.1, 0.3, 0.5, 0.7, 0.9].map((t) => heatColor(t, 1))
  return (
    <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
      <span>Quiet</span>
      <div className="flex items-center gap-0.5">
        {swatches.map((cls, i) => (
          <span key={i} className={cn('inline-block h-3 w-5 rounded-sm', cls)} aria-hidden="true" />
        ))}
      </div>
      <span>Busy{max > 0 ? <> · up to {compactBare(max)}</> : null}</span>
    </div>
  )
}

// Warm "heat" ramp: pale amber → deep red. A heatmap should look like heat, so
// "hotter = busier" reads instantly without decoding an arbitrary hue order.
function heatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return 'bg-muted/40'
  const intensity = value / max
  if (intensity < 0.2) return 'bg-amber-100 dark:bg-amber-950/50'
  if (intensity < 0.4) return 'bg-amber-200 dark:bg-amber-900/60'
  if (intensity < 0.6) return 'bg-orange-300 dark:bg-orange-800/70'
  if (intensity < 0.8) return 'bg-orange-500 dark:bg-orange-600/85'
  return 'bg-red-600 dark:bg-red-500'
}

// Locale-aware compact magnitude. PKR audiences read in lakh / crore; pass
// 'COUNT' (or any non-PKR code) for plain K/M. Trailing ".0" is dropped.
function formatCompact(value: number, code: string): string {
  const v = Math.abs(value)
  const s = value < 0 ? '-' : ''
  const trim = (n: number) => String(Math.round(n * 10) / 10)
  if (code === 'PKR') {
    if (v >= 1_00_00_000) return `${s}${trim(v / 1_00_00_000)} Cr`
    if (v >= 1_00_000) return `${s}${trim(v / 1_00_000)} Lac`
    if (v >= 1_000) return `${s}${trim(v / 1_000)}k`
    return `${s}${Math.round(v)}`
  }
  if (v >= 1_000_000) return `${s}${trim(v / 1_000_000)}M`
  if (v >= 1_000) return `${s}${trim(v / 1_000)}K`
  return `${s}${Math.round(v)}`
}

function formatHourShort(hour: number): string {
  const h = ((Math.floor(hour) % 24) + 24) % 24
  const period = h < 12 ? 'a' : 'p'
  const display = h % 12 === 0 ? 12 : h % 12
  return `${display}${period}`
}

function buildHourlyPdf(
  matrix: HourlyHeatmapCell[][],
  metric: Metric,
  formatCurrency: (n: number) => string,
): string {
  const headerCells = ['', ...HOUR_LABELS.map((h) => formatHour12(h))]
    .map((h) => `<th class="num">${escapeHtml(h)}</th>`)
    .join('')
  const rows = matrix
    .map((row, dow) => {
      const cells = row
        .map((c) => {
          const v = metric === 'net' ? c.net : c.orders
          const display =
            metric === 'net' ? formatCurrency(v) : String(Math.round(v * 10) / 10)
          return `<td class="num">${v > 0 ? escapeHtml(display) : ''}</td>`
        })
        .join('')
      return `<tr><th>${DOW_LABELS[dow]}</th>${cells}</tr>`
    })
    .join('')
  return `
    <table>
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `
}
